import React, { useState } from 'react';
import { useAICoworkerStats } from 'flow/hooks/api/useAICoworkerStats';
import { ExtensionKey } from 'flow/types/EntityKeys';
import StageProgress from 'flow/components/guide/StageProgress';
import useAppContext from 'flow/hooks/useAppContext';
import IconButton from 'flow/components/ui-components/IconButton';
import Icon from 'flow/components/ui-components/Icon';
import AIStatsCallCount from 'flow/components/ai-coworker/AIStatsCallCount';
import { useAiCoworkerAggregatedEvals } from 'flow/hooks/api/useAiCoworkerAggregatedEvals';
import AIEvalsResolutionRate from 'flow/components/ai-coworker/AIEvalsResolutionRate';
import AIEvalsSatisfactionRate from 'flow/components/ai-coworker/AIEvalsSatisfactionRate';

const intervals = ['today', 'week', 'month'] as const;

const intervalHeaders = [
    'Queue.Statistics.Interval.Today',
    'Queue.Statistics.Interval.Week',
    'Queue.Statistics.Interval.Month',
] as const;

interface Props {
    extensionKey: ExtensionKey;
}

const AICoworkerAgentStatisticsPreview = ({ extensionKey }: Props) => {
    const { i18next } = useAppContext();
    const [current, setCurrent] = useState(0);
    const currentInterval = intervals[current];

    const { data: stats, isLoading } = useAICoworkerStats(
        extensionKey,
        currentInterval,
    );

    const { data: evals, isLoading: isLoadingEvals } =
        useAiCoworkerAggregatedEvals(extensionKey, currentInterval);

    const statisticsIntervalChange = (intervalIndex: number) => {
        setCurrent(intervalIndex);
    };

    const handleForward = (event: React.MouseEvent) => {
        event.preventDefault();
        const newIndex = current + 1;
        const newIntervalIndex = Math.min(newIndex, intervals.length - 1);
        setCurrent(newIntervalIndex);
    };

    const handleBack = (event: React.MouseEvent) => {
        event.preventDefault();
        const newIndex = current - 1;
        const newIntervalIndex = Math.max(newIndex, 0);
        setCurrent(newIntervalIndex);
    };

    return (
        <div className="shadow-base flex w-full flex-col gap-3 rounded-xl bg-white p-6 dark:bg-gray-100 dark:text-gray-100">
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    {current > 0 ? (
                        <IconButton
                            aria-label={i18next.t('action.previous')}
                            onClick={handleBack}
                            className="text-gray-800 dark:text-gray-700"
                        >
                            <Icon iconKey="arrow_back" />
                        </IconButton>
                    ) : (
                        <div className="size-8" />
                    )}
                    <div className="bg-pbx-huntgroup flex rounded-md px-3 py-1 text-xs font-medium text-white">
                        {i18next.t(intervalHeaders[current])}
                    </div>
                    {current + 1 !== intervals.length ? (
                        <IconButton
                            aria-label={i18next.t('action.next')}
                            onClick={handleForward}
                            className="text-gray-800 dark:text-gray-700"
                        >
                            <Icon iconKey="arrow_forward" />
                        </IconButton>
                    ) : (
                        <div className="size-8" />
                    )}
                </div>
                <div className="flex w-full items-end justify-around gap-4">
                    <AIStatsCallCount
                        count={stats?.numberOfCalls ?? 0}
                        isLoading={isLoading}
                        hasStats={!!stats}
                    />
                    <AIEvalsResolutionRate
                        resolutionRate={evals?.resolutionRate}
                        isLoading={isLoadingEvals}
                        hasStats={!!evals}
                    />
                    <AIEvalsSatisfactionRate
                        satisfactionRate={evals?.satisfactionRate}
                        isLoading={isLoadingEvals}
                        hasStats={!!evals}
                    />
                </div>
            </div>

            <div className="flex items-center justify-center pt-2">
                <StageProgress
                    stages={intervals.length}
                    currentStage={current}
                    handleSelect={statisticsIntervalChange}
                    selectedColor="bg-pbx-huntgroup"
                />
            </div>
        </div>
    );
};

export default AICoworkerAgentStatisticsPreview;
